import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
  Badge,
  FormGroup,
  Label,
} from "reactstrap";
import { Search } from "react-feather";
import axios from "axios";
import { baseURL } from "../../../../../Services/api/baseURL";
import { toast } from "react-toastify";

const AddCustomersToTagModal = ({ isOpen, toggle, tag, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setSelectedCustomers([]);
      setSearchTerm("");
      setPage(1);
      fetchCustomers();
    }
  }, [isOpen]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (isOpen) {
        setPage(1);
        fetchCustomers(true);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const fetchCustomers = async (reset = false) => {
    setIsLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.get(
        `${baseURL}/api/customers/get-customers-list`,
        {
          params: {
            page: reset ? 1 : page,
            limit: 20,
            search: searchTerm,
          },
          headers: { Authorization: token },
        }
      );

      if (response.status === 200) {
        const newCustomers = response.data.data.customerList;
        
        if (reset) {
          setCustomers(newCustomers);
        } else {
          setCustomers((prev) => [...prev, ...newCustomers]);
        }
        
        setHasMore(newCustomers.length === 20);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
    fetchCustomers();
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers((prev) => {
      if (prev.includes(customerId)) {
        return prev.filter((id) => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map((c) => c._id));
    }
  };

  const handleAddCustomers = async () => {
    if (selectedCustomers.length === 0) {
      toast.error("Please select at least one customer");
      return;
    }

    setIsSaving(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.post(
        `${baseURL}/api/tags/bulk/add`,
        {
          customerIds: selectedCustomers,
          tagId: tag._id,
        },
        { headers: { Authorization: token } }
      );

      if (response.status === 200) {
        toast.success(
          `Tag added to ${response.data.data.modified} customer(s)`
        );
        if (onSuccess) onSuccess();
        toggle();
      }
    } catch (error) {
      toast.error("Failed to add customers");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const customerHasTag = (customer) => {
    if (!customer.tags || customer.tags.length === 0) return false;
    return customer.tags.some((t) => {
      const tagId = typeof t === "object" ? t._id : t;
      return tagId === tag._id;
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: tag?.color || "#6B7280",
            }}
          ></span>
          Add Customers to "{tag?.display_name}"
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Search */}
        <div className="mb-3">
          <div className="position-relative">
            <Search
              size={18}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#6c757d",
              }}
            />
            <Input
              type="text"
              placeholder="Search customers by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: "40px" }}
            />
          </div>
        </div>

        {/* Selected Count */}
        {selectedCustomers.length > 0 && (
          <div className="mb-3">
            <Badge color="primary" style={{ fontSize: "0.9rem", padding: "8px 12px" }}>
              {selectedCustomers.length} customer(s) selected
            </Badge>
          </div>
        )}

        {/* Select All */}
        <div className="mb-3">
          <FormGroup check>
            <Input
              type="checkbox"
              id="selectAll"
              checked={
                customers.length > 0 &&
                selectedCustomers.length === customers.length
              }
              onChange={handleSelectAll}
            />
            <Label check for="selectAll" style={{ fontWeight: "500" }}>
              Select All ({customers.length})
            </Label>
          </FormGroup>
        </div>

        {/* Customer List */}
        <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
          }}
        >
          {isLoading && page === 1 ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No customers found
            </div>
          ) : (
            <>
              {customers.map((customer) => {
                const hasTag = customerHasTag(customer);
                const isSelected = selectedCustomers.includes(customer._id);
                const fullName = customer.firstName && customer.lastName
                  ? `${customer.firstName} ${customer.lastName}`
                  : customer.firstName || customer.lastName || "N/A";

                return (
                  <div
                    key={customer._id}
                    onClick={() => !hasTag && handleSelectCustomer(customer._id)}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid #e0e0e0",
                      cursor: hasTag ? "not-allowed" : "pointer",
                      backgroundColor: isSelected
                        ? "#e3f2fd"
                        : hasTag
                        ? "#f5f5f5"
                        : "#fff",
                      opacity: hasTag ? 0.6 : 1,
                      transition: "all 0.2s",
                    }}
                    className={!hasTag ? "hover-shadow" : ""}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-3 flex-grow-1">
                        <Input
                          type="checkbox"
                          checked={isSelected}
                          disabled={hasTag}
                          onChange={() => handleSelectCustomer(customer._id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: hasTag ? "not-allowed" : "pointer" }}
                        />
                        <div className="flex-grow-1">
                          <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                            {fullName}
                            {hasTag && (
                              <Badge color="success" className="ms-2">
                                Already has tag
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                            {customer.email || "No email"}
                            {customer.phoneNumber && (
                              <> • {customer.phoneNumber}</>
                            )}
                          </div>
                          {customer.totalOrders > 0 && (
                            <div style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: "4px" }}>
                              {customer.totalOrders} orders • $
                              {customer.totalAmount?.toFixed(2) || "0.00"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Load More */}
              {hasMore && !isLoading && (
                <div className="text-center py-3">
                  <Button
                    color="link"
                    onClick={handleLoadMore}
                    style={{ textDecoration: "none" }}
                  >
                    Load More
                  </Button>
                </div>
              )}

              {isLoading && page > 1 && (
                <div className="text-center py-3">
                  <Spinner size="sm" color="primary" />
                </div>
              )}
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button className="cancel_Btn" onClick={toggle} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          className="btn btn-primary"
          onClick={handleAddCustomers}
          disabled={isSaving || selectedCustomers.length === 0}
        >
          {isSaving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Adding...
            </>
          ) : (
            `Add ${selectedCustomers.length} Customer(s)`
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddCustomersToTagModal;